import React, { useState } from "react";

const TvRepairForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    service: "",
    problem: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone.trim())) {
      newErrors.phone = "Enter a valid 10-digit number";
    }
    if (!formData.service) newErrors.service = "Please select a service";
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build WhatsApp message
    let message = `Hi, I need repair/service.\n\n`;
    message += `👤 Name: ${formData.name}\n`;
    message += `📞 Phone: ${formData.phone}\n`;
    message += `🔧 Service: ${formData.service}\n`;
    if (formData.problem) message += `⚠️ Problem: ${formData.problem}\n`;
    if (formData.description) message += `📝 Details: ${formData.description}\n`;
    message += `\nPlease schedule a repair visit. Thank you!`;

    const whatsappUrl = `https://wa.me/918007113950?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");

    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const getLcdLedProblems = () => [
    "Not getting on",
    "Picture not visible",
    "Backlights issue",
    "Display broken / cracked",
    "Horizontal lines on screen",
    "No sound",
    "Remote not working",
    "Software / Smart TV issue",
    "Other / Not sure",
  ];

  const getTvInstallationProblems = () => [
    "24 inch to 43 inch",
    "43 inch to 75 inch",
    "Wall mounting",
    "Unboxing & setup",
  ];

  const getWashingMachineProblems = () => [
    "Not turning on",
    "Water leakage",
    "Drum not spinning",
    "Excessive vibration / noise",
    "Water not draining",
    "Error code on display",
    "Door / lid not opening",
    "Motor issue",
    "Timer / control panel issue",
    "Other / Not sure",
  ];

  const problems =
    formData.service === "TV Installation"
      ? getTvInstallationProblems()
      : formData.service === "Washing Machine Repair"
      ? getWashingMachineProblems()
      : getLcdLedProblems();

  return (
    <div className="booking-form-card">
      <h3 className="booking-form-title">📋 Book Your Repair</h3>
      <p className="booking-form-subtitle">
        Fill the form & we'll connect with you on WhatsApp instantly
      </p>

      {submitted && (
        <div
          style={{
            padding: "12px 16px",
            background: "#ECFDF5",
            border: "1px solid #A7F3D0",
            borderRadius: "var(--radius-sm)",
            color: "#065F46",
            fontSize: "var(--font-size-sm)",
            fontWeight: 600,
            marginBottom: "var(--space-lg)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          ✅ Redirecting to WhatsApp...
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="repair-name">Full Name *</label>
            <input
              id="repair-name"
              type="text"
              name="name"
              className="form-input"
              placeholder="Enter your name"
              value={formData.name}
              onChange={handleChange}
            />
            {errors.name && <div className="form-error">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="repair-phone">Mobile Number *</label>
            <input
              id="repair-phone"
              type="tel"
              name="phone"
              className="form-input"
              placeholder="10-digit mobile number"
              value={formData.phone}
              onChange={handleChange}
              maxLength={10}
              inputMode="numeric"
              pattern="[0-9]*"
            />
            {errors.phone && <div className="form-error">{errors.phone}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="repair-service">Service Type *</label>
            <select
              id="repair-service"
              name="service"
              className="form-select"
              value={formData.service}
              onChange={(e) => {
                handleChange(e);
                setFormData((prev) => ({ ...prev, problem: "" }));
              }}
            >
              <option value="" disabled>Select service</option>
              <option value="LED TV Repair">LED TV Repair</option>
              <option value="LCD TV Repair">LCD TV Repair</option>
              <option value="Smart TV Issues">Smart TV Issues</option>
              <option value="Screen Replacement">Screen Replacement</option>
              <option value="TV Installation">TV Installation</option>
              <option value="Washing Machine Repair">Washing Machine Repair</option>
            </select>
            {errors.service && <div className="form-error">{errors.service}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="repair-problem">Problem</label>
            <select
              id="repair-problem"
              name="problem"
              className="form-select"
              value={formData.problem}
              onChange={handleChange}
            >
              <option value="">Select problem (optional)</option>
              {problems.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="repair-description">Additional Details</label>
          <textarea
            id="repair-description"
            name="description"
            className="form-textarea"
            placeholder="Describe your TV / washing machine issue briefly (optional)"
            value={formData.description}
            onChange={handleChange}
            rows={3}
          />
        </div>

        <button type="submit" className="btn btn-whatsapp btn-lg form-submit-btn">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          Book via WhatsApp
        </button>
      </form>
    </div>
  );
};

export default TvRepairForm;
